// kfe-login.js — real login screen (Brief 188).
//
// PORT TARGET: iOS KFELoginViewController.m / KFELoginManager.m — username +
// password fields, orange "Log In" submit, "Forgot Password?" link (Rule #9).
//
// Flow: form → crmWebLogin() → authStore.setSession() → router.navigate().
// crm-client.js stays DOM-free (Rule #1); this screen is the one place that
// turns its CrmResult into UI state and a route change.

import { KFEComponent } from "../core/component.js";
import { html } from "../core/html.js";
import { t } from "../core/i18n.js";
import * as router from "../router.js";
import { crmWebLogin } from "../data/crm-client.js";
import { setSession } from "../data/auth-store.js";

export class KFELogin extends KFEComponent {
  static styles = `
    :host { display: block; max-width: 22rem; margin: 0 auto; }
    .field { display: flex; flex-direction: column; gap: 0.25rem; margin-bottom: 1rem; }
    label { color: var(--kfe-muted); font-size: 0.85rem; }
    input[type="text"], input[type="password"] {
      box-sizing: border-box;
      min-height: 44px;
      padding: 0.6rem 0.75rem;
      border: 1px solid var(--kfe-border);
      border-radius: var(--kfe-radius);
      background: var(--kfe-surface);
      color: var(--kfe-fg);
      font: inherit;
    }
    input:focus { outline: 2px solid var(--kfe-accent); outline-offset: 1px; }
    input[disabled] { opacity: 0.6; }
    /* No dedicated error token in tokens.css — a readable red-on-dark. */
    .error { color: #e15c5c; font-size: 0.85rem; margin: -0.5rem 0 1rem; }
    .actions { display: flex; flex-direction: column; gap: 0.75rem; margin-top: 0.5rem; }
    button.primary {
      min-height: 44px;
      padding: 0.6rem 1.25rem;
      border-radius: var(--kfe-radius);
      border: 1px solid var(--kfe-accent);
      background: var(--kfe-accent);
      color: var(--kfe-on-accent);
      font: inherit; font-weight: 600;
      cursor: pointer;
    }
    button.primary[disabled] { opacity: 0.5; cursor: not-allowed; }
    button.secondary {
      padding: 0.5rem 1rem;
      border-radius: var(--kfe-radius);
      border: 1px solid var(--kfe-border);
      background: transparent;
      color: var(--kfe-fg);
      font: inherit;
      cursor: pointer;
    }
    a.forgot { color: var(--kfe-link); text-align: center; text-decoration: none; }
  `;

  /** @type {string} */ _username = "";
  /** @type {string} */ _password = "";
  /** @type {boolean} */ _submitting = false;
  /** @type {string} */ _error = "";

  onConnected() {
    this.dispatchEvent(new CustomEvent("kfe:set-header-title", {
      detail: { title: t("login.title") },
      bubbles: true,
      composed: true,
    }));

    const onI18n = () => this._scheduleRender();
    document.addEventListener("kfe:i18n", onI18n);
    this.addCleanup(() => document.removeEventListener("kfe:i18n", onI18n));
  }

  /** @param {Event} e */
  _onUsernameInput = (e) => {
    this._username = /** @type {HTMLInputElement} */ (e.target).value;
  };

  /** @param {Event} e */
  _onPasswordInput = (e) => {
    this._password = /** @type {HTMLInputElement} */ (e.target).value;
  };

  /** @returns {string | null} */
  _returnPath() {
    const raw = new URLSearchParams(location.search).get("return");
    return raw ? decodeURIComponent(raw) : null;
  }

  /** @param {Event} e */
  _onSubmit = async (e) => {
    e.preventDefault();
    if (this._submitting) return;
    const username = this._username.trim();
    const password = this._password;
    if (!username || !password) return;

    this._submitting = true;
    this._error = "";
    this._scheduleRender();

    const result = await crmWebLogin({ username, password });

    this._submitting = false;

    if (result.success) {
      setSession(result.data);
      // Brief 025: announce the signed-IN transition. `kfe:auth-changed` was
      // previously dispatched only on sign-OUT (auth-store.signOut), so the app
      // shell had no signal to start the sync engine on. Dispatch AFTER
      // setSession so every listener sees a live session, and BEFORE navigate so
      // the login pull is already in flight while the pathway screen mounts.
      document.dispatchEvent(new Event("kfe:auth-changed"));
      router.navigate(this._returnPath() ?? "/");
      return;
    }

    // Error-kind copy map. bad_credentials keeps its dedicated i18n key
    // (already localized to zh-Hans, matches iOS canon "用户名或密码不正确。").
    // For account_locked / rate_limited / validation_error / progress_conflict
    // the server's `message` field is Allen's canonical error copy (server
    // localizes based on request context) — prefer it over inventing
    // translations. English fallbacks kick in only if the server omits `message`.
    // network / generic / session_displaced (rare at login time) fall back
    // to the generic error and the learner stays on the screen.
    const errKind = result.error?.kind;
    const serverMsg = result.error?.message;
    if (errKind === "bad_credentials") {
      this._error = t("login.errorBadCredentials");
    } else if (errKind === "account_locked") {
      this._error = serverMsg || "Your account is locked. Please contact support.";
    } else if (errKind === "rate_limited") {
      this._error = serverMsg || "Too many attempts. Please wait a moment and try again.";
    } else if (errKind === "validation_error") {
      this._error = serverMsg || t("login.errorGeneric");
    } else if (errKind === "progress_conflict") {
      // 91048 — the CRM refuses login because another device has unsynced
      // progress. Full three-option resolution UI (wait / accept_loss /
      // reconcile) lives in a follow-up brief; here we surface the server
      // message + previous_device hint so the learner knows what's wrong and
      // what to try, instead of a generic error.
      //
      // Two things to know about the wire shape:
      //   1. The server's 91048 body wraps the conflict info in a top-level
      //      `data` field: { message, error_code, data: { previous_device,
      //      options, ... } }. crm-client.js preserves the WHOLE body as
      //      error.data, so previous_device lives at error.data.data.previous_device
      //      (the doubled "data" reflects wrapper + inner-payload nesting).
      //   2. Read the raw server message from error.data.message (top-level
      //      of the preserved body) rather than error.message — crm-client.js
      //      sets error.message to a "Progress conflict" default when the
      //      server omits it, which would shadow the informative English
      //      fallback below.
      const prevDevice = result.error?.data?.data?.previous_device;
      const model = prevDevice?.device_model || "another device";
      const rawServerMsg = result.error?.data?.message;
      this._error = rawServerMsg
        || `You have unsynced progress on ${model}. Please open that device to sync first, or contact support to switch devices.`;
    } else {
      this._error = t("login.errorGeneric");
    }
    this._scheduleRender();
  };

  _onBack = () => {
    router.navigate("/");
  };

  render() {
    return html`
      <form on:submit=${this._onSubmit}>
        <div class="field">
          <label for="login-username">${t("login.username")}</label>
          <input id="login-username" name="username" type="text" autocomplete="username"
                 .value=${this._username} .disabled=${this._submitting}
                 on:input=${this._onUsernameInput} />
        </div>
        <div class="field">
          <label for="login-password">${t("login.password")}</label>
          <input id="login-password" name="password" type="password" autocomplete="current-password"
                 .value=${this._password} .disabled=${this._submitting}
                 on:input=${this._onPasswordInput} />
        </div>
        ${this._error ? html`<p class="error">${this._error}</p>` : ""}
        <div class="actions">
          <button class="primary" type="submit" .disabled=${this._submitting}>${t("login.submit")}</button>
          <a class="forgot" href="/forgot-password">${t("login.forgot")}</a>
          <button class="secondary" type="button" on:click=${this._onBack}>${t("nav.back")}</button>
        </div>
      </form>
    `;
  }
}
